import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { profileLabel } from "@/components/ProfileTypePicker";
import { FinloMark } from "@/components/FinloLogo";
import DemoBanner from "@/components/DemoBanner";
import CurrencyMenu from "@/components/CurrencyMenu";
import { Briefcase, Compass, Crown, UserCog, Landmark, Bell, ChevronDown, LogOut, Plus, ShieldCheck, User, SlidersHorizontal, GraduationCap } from "lucide-react";

export default function Navbar({ accountCount = 0, unreadAlerts = 0, onConnect, onOpenAlerts, onOpenBudget, onOpenStudent, onOpenPro, onOpenBilling, onOpenAdmin, onOpenProfileType, onReplayTour, view = "dashboard" }) {
  const { user, logout } = useAuth();
  const isStudent = user?.profile_type === "student";
  const isPro = user?.profile_type === "professional";
  const [menu, setMenu] = useState(false);

  return (
    <header className="sticky top-0 z-40 bg-[#0D0D0D]/85 backdrop-blur-2xl border-b border-white/[0.08]">
      {user?.is_demo && <DemoBanner />}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5 min-w-0">
          <FinloMark />
          <div className="leading-tight min-w-0">
            <div data-testid="navbar-brand" className="font-display font-bold tracking-tight truncate text-white">Finlo<span className="text-[#00C896]">.</span></div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#5F6C7D] -mt-0.5 truncate hidden sm:block">Ventiqa Solutions</div>
          </div>
        </div>

        <div className="flex items-center gap-2 sm:gap-3">
          <button
            data-testid="nav-budget-link"
            onClick={onOpenBudget}
            className={`hidden sm:inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition ${
              view === "budget" ? "bg-[#00C896]/15 text-[#4DE3BF] border border-[#00C896]/30" : "text-slate-400 hover:text-white border border-transparent"
            }`}
          >
            <SlidersHorizontal className="h-3.5 w-3.5" /> Budgets
          </button>

          {isPro && (<button
            data-testid="nav-pro-link"
            onClick={onOpenPro}
            className={`hidden sm:inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition ${
              view === "pro" ? "bg-[#00C896]/15 text-[#4DE3BF] border border-[#00C896]/30" : "text-slate-400 hover:text-white border border-transparent"
            }`}
          >
            <Briefcase className="h-3.5 w-3.5" /> Pro Hub
          </button>)}

          {isStudent && (<button
            data-testid="nav-student-link"
            onClick={onOpenStudent}
            className={`hidden sm:inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition ${
              view === "student" ? "bg-[#00C896]/15 text-[#4DE3BF] border border-[#00C896]/30" : "text-slate-400 hover:text-white border border-transparent"
            }`}
          >
            <GraduationCap className="h-3.5 w-3.5" /> Student Hub
          </button>)}

          {accountCount > 0 && (
            <div className="hidden md:flex items-center gap-1.5 rounded-full border border-[#00C896]/30 bg-[#00C896]/10 px-3 py-1.5 text-xs text-[#4DE3BF]">
              <ShieldCheck className="h-3.5 w-3.5" />
              TrueLayer connected · {accountCount} {accountCount === 1 ? "account" : "accounts"}
            </div>
          )}

          <Button
            data-testid="navbar-connect-button"
            onClick={onConnect}
            size="sm"
            className="rounded-full bg-[#00C896] hover:bg-[#00C896] text-[#04140D] font-semibold"
          >
            <Plus className="h-4 w-4 sm:mr-1.5" />
            <span className="hidden sm:inline">Connect bank</span>
          </Button>

          <button
            data-testid="navbar-alerts-pill"
            onClick={onOpenAlerts}
            className="relative rounded-full border border-white/10 bg-[#14181D] h-9 w-9 flex items-center justify-center text-slate-300 hover:text-white hover:border-[#00C896]/40 transition"
          >
            <Bell className="h-4 w-4" />
            {unreadAlerts > 0 && (
              <span data-testid="alerts-unread-count" className="absolute -top-1 -right-1 h-4 min-w-4 px-1 rounded-full bg-rose-500 text-[10px] font-bold flex items-center justify-center text-white">
                {unreadAlerts}
              </span>
            )}
          </button>

          <CurrencyMenu />

          {user?.premium?.active ? (
            <span data-testid="nav-premium-badge" className="hidden sm:inline-flex items-center gap-1 rounded-full border border-[#00C896]/40 bg-[#00C896]/10 px-2.5 py-1 text-[11px] font-semibold text-[#4DE3BF]">
              <Crown className="h-3.5 w-3.5" /> Premium
            </span>
          ) : (
            <button data-testid="nav-go-premium" onClick={onOpenBilling}
              className="hidden sm:inline-flex items-center gap-1.5 rounded-full border border-[#00C896]/40 px-3 py-1.5 text-xs font-semibold text-[#00C896] hover:bg-[#00C896]/10 transition-colors">
              <Crown className="h-3.5 w-3.5" /> Go Premium
            </button>
          )}

          <div className="relative">
            <button
              data-testid="navbar-user-menu"
              onClick={() => setMenu((m) => !m)}
              className="flex items-center gap-1.5 rounded-full border border-white/10 bg-[#14181D] pl-1 pr-2 py-1 hover:border-[#00C896]/40 transition"
            >
              <span className="relative h-7 w-7 rounded-full bg-[#00C896]/20 flex items-center justify-center text-[#4DE3BF]">
                <User className="h-4 w-4" />
                {user?.premium?.active && (
                  <span data-testid="nav-premium-avatar-crown" className="sm:hidden absolute -top-1 -right-1 h-3.5 w-3.5 rounded-full bg-[#00C896] flex items-center justify-center">
                    <Crown className="h-2.5 w-2.5 text-[#04140D]" />
                  </span>
                )}
              </span>
              <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
            </button>
            <AnimatePresence>
              {menu && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setMenu(false)} />
                  <motion.div
                    initial={{ opacity: 0, y: -6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                    className="absolute right-0 mt-2 w-56 rounded-xl border border-white/10 bg-[#14181D] shadow-xl z-50 p-1.5"
                  >
                    <div className="px-3 py-2 border-b border-white/8">
                      <div className="text-sm font-medium truncate">{user?.name}</div>
                      <div className="text-xs text-slate-500 truncate">{user?.email}</div>
                    </div>
                    <div className="px-3 py-2 flex items-center gap-2 text-xs text-slate-400">
                      <Landmark className="h-3.5 w-3.5" /> Region: <span className="text-[#4DE3BF] font-medium">{user?.region === "UK" ? "United Kingdom" : "Non-UK"}</span>
                    </div>
                    <button
                      data-testid="nav-budget-menu-item"
                      onClick={() => { setMenu(false); onOpenBudget?.(); }}
                      className="w-full flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-slate-200 hover:bg-white/5 transition sm:hidden"
                    >
                      <SlidersHorizontal className="h-4 w-4" /> Budget settings
                    </button>
                    <button
                      data-testid="nav-profile-type-menu-item"
                      onClick={() => { setMenu(false); onOpenProfileType?.(); }}
                      className="w-full flex items-center justify-between gap-2 rounded-lg px-3 py-2 text-sm text-slate-200 hover:bg-white/5 transition"
                    >
                      <span className="flex items-center gap-2"><UserCog className="h-4 w-4" /> Profile type</span>
                      <span data-testid="nav-profile-type-value" className="text-[11px] text-[#4DE3BF]">{profileLabel(user?.profile_type)}</span>
                    </button>
                    <button
                      data-testid="nav-billing-menu-item"
                      onClick={() => { setMenu(false); onOpenBilling?.(); }}
                      className="w-full flex items-center justify-between gap-2 rounded-lg px-3 py-2 text-sm text-slate-200 hover:bg-white/5 transition"
                    >
                      <span className="flex items-center gap-2"><Crown className="h-4 w-4" /> Premium & billing</span>
                      {user?.premium?.active && <span className="text-[11px] text-[#4DE3BF]">Active</span>}
                    </button>
                    {user?.is_admin && (
                      <button
                        data-testid="nav-admin-menu-item"
                        onClick={() => { setMenu(false); onOpenAdmin?.(); }}
                        className="w-full flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-slate-200 hover:bg-white/5 transition"
                      >
                        <ShieldCheck className="h-4 w-4" /> Admin
                      </button>
                    )}
                    <button
                      data-testid="nav-replay-tour-menu-item"
                      onClick={() => { setMenu(false); onReplayTour?.(); }}
                      className="w-full flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-slate-200 hover:bg-white/5 transition"
                    >
                      <Compass className="h-4 w-4" /> Replay welcome tour
                    </button>
                    {isPro && (<button
                      data-testid="nav-pro-menu-item"
                      onClick={() => { setMenu(false); onOpenPro?.(); }}
                      className="w-full flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-slate-200 hover:bg-white/5 transition sm:hidden"
                    >
                      <Briefcase className="h-4 w-4" /> Professional Hub
                    </button>)}
                    {isStudent && (<button
                      data-testid="nav-student-menu-item"
                      onClick={() => { setMenu(false); onOpenStudent?.(); }}
                      className="w-full flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-slate-200 hover:bg-white/5 transition sm:hidden"
                    >
                      <GraduationCap className="h-4 w-4" /> Student Hub
                    </button>)}
                    <button
                      data-testid="logout-button"
                      onClick={() => { setMenu(false); logout(); }}
                      className="w-full flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-rose-300 hover:bg-rose-500/10 transition"
                    >
                      <LogOut className="h-4 w-4" /> Sign out
                    </button>
                  </motion.div>
                </>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </header>
  );
}
